import React, { useEffect, useState } from 'react';
import './Orders.css';

function Orders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({});

    const fetchOrders = async (pageNumber = 1) => {
        setLoading(true);
        try {
            const res = await fetch(`https://pentadeco.in/api/allorders?page=${pageNumber}&limit=20`);
            const data = await res.json();
            if (data.success) {
                setOrders(data.orders);
                setPagination(data.pagination);
            }
        } catch (err) {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchOrders(page); }, [page]);

    const handleNext = () => { if (pagination.hasNextPage) setPage(pagination.nextPage); };
    const handlePrev = () => { if (pagination.hasPrevPage) setPage(pagination.prevPage); };

    const changeOrderStatus = async (orderid, newStatus) => {
        try {
            const res = await fetch(`https://pentadeco.in/api/orderstate/${orderid}/${newStatus}`);
            const data = await res.json();
            if (data.success) {
                setOrders(prev => prev.map(o => o.orderid === orderid ? { ...o, status: newStatus } : o));
            } else {
                alert(data.msg || 'Failed to update status');
            }
        } catch (err) {
            alert('Error updating status');
        }
    };

    const statusClass = (status) => {
        switch (status) {
            case 'pending':    return 'text-warning border-warning bg-warning-subtle';
            case 'confirmed':  return 'text-primary border-primary bg-primary-subtle';
            case 'packed':     return 'text-info border-info bg-info-subtle';
            case 'dispatched': return 'text-secondary border-secondary bg-secondary-subtle';
            case 'delivered':  return 'text-success border-success bg-success-subtle';
            case 'cancelled':  return 'text-danger border-danger bg-danger-subtle';
            default:           return 'text-dark border-dark bg-dark-subtle';
        }
    };

    const statusSelect = (order) => (
        <select
            className={`order-status-select form-select ${statusClass(order.status)}`}
            value={order.status}
            onChange={e => changeOrderStatus(order.orderid, e.target.value)}
        >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="packed">Packed</option>
            <option value="dispatched">Dispatched</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
        </select>
    );

    const Pagination = () => (
        <div className="orders-pagination">
            <button className="orders-page-btn" onClick={handlePrev} disabled={!pagination?.hasPrevPage}>
                <i className="fi fi-ss-angle-left" style={{position:'relative', bottom:'-2px'}}></i>
            </button>
            <span className="orders-page-info">Page {pagination?.currentPage} of {pagination?.totalPages}</span>
            <button className="orders-page-btn" onClick={handleNext} disabled={!pagination?.hasNextPage}>
                <i className="fi fi-ss-angle-right" style={{position:'relative', bottom:'-2px'}}></i>
            </button>
        </div>
    );

    return (
        <div className='main-orders'>
            <h2 className="body-font-bold fs-4 mt-3 mb-4">Orders</h2>

            {loading ? (
                <div className="text-center py-5 text-muted">Loading orders...</div>
            ) : (
                <>
                    {/* ── Desktop table ── */}
                    <div className="orders-desktop-view">
                        <div className="orders-table-wrap">
                            <div className="orders-table-head">
                                <span>DATE & TIME</span>
                                <span>ORDER ID</span>
                                <span>CUSTOMER</span>
                                <span>PRODUCTS</span>
                                <span>PRICE</span>
                                <span>STATUS</span>
                            </div>
                            {orders.map(order => {
                                const rawPrice = order.products.reduce((s, p) => s + (p.price * p.quantity), 0);
                                const discount = order.discount || 0;
                                const finalPrice = rawPrice - discount;
                                return (
                                    <div className="orders-table-row" key={order.orderid}>
                                        <span className="order-cell-muted">{new Date(order.orderdate).toLocaleString()}</span>
                                        <span className="order-id-text">{order.orderid}</span>
                                        <div>
                                            <div style={{fontWeight:700, fontSize:'0.82rem'}}>{order.address?.name}</div>
                                            <div className="order-cell-muted">{order.number}</div>
                                            <div className="order-cell-muted" style={{lineHeight:'1.4', marginTop:'4px'}}>{order.address?.address}</div>
                                            <div className="order-cell-muted">City: {order.address?.city || 'N/A'}</div>
                                        </div>
                                        <div>
                                            {order.products.map((p, idx) => (
                                                <div key={idx} className="order-cell-muted">{p.proname} ×{p.quantity} — ₹{p.price}</div>
                                            ))}
                                        </div>
                                        <div>
                                            {discount > 0 ? (
                                                <>
                                                    <span className="order-price-strike">₹{rawPrice}</span><br />
                                                    <span className="order-price-final">₹{finalPrice}</span><br />
                                                    <span className="badge bg-success mt-1" style={{fontSize:'0.6rem'}}>{order.coupon} −{discount}</span>
                                                </>
                                            ) : <span style={{fontWeight:700}}>₹{rawPrice}</span>}
                                        </div>
                                        <div>{statusSelect(order)}</div>
                                    </div>
                                );
                            })}
                        </div>
                        <Pagination />
                    </div>

                    {/* ── Mobile cards ── */}
                    <div className="orders-mobile-view">
                        {orders.map(order => {
                            const rawPrice = order.products.reduce((s, p) => s + (p.price * p.quantity), 0);
                            const discount = order.discount || 0;
                            const finalPrice = rawPrice - discount;
                            return (
                                <div className="order-card" key={order.orderid}>
                                    <div className="order-card-header">
                                        <div>
                                            <div className="order-card-id">{order.orderid}</div>
                                            <div className="order-card-date">{new Date(order.orderdate).toLocaleString()}</div>
                                        </div>
                                    </div>
                                    <div className="order-card-name">{order.address?.name}</div>
                                    <div className="order-card-address">
                                        {order.number} &nbsp;·&nbsp; {order.address?.address}{order.address?.city ? `, ${order.address.city}` : ''}
                                    </div>
                                    <div className="order-card-products">
                                        {order.products.map((p, idx) => (
                                            <div key={idx} className="order-card-product-item">
                                                {p.proname} ×{p.quantity} — ₹{p.price}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="order-card-footer">
                                        <div className="order-card-price">
                                            {discount > 0 ? (
                                                <>
                                                    <span className="order-price-strike me-1">₹{rawPrice}</span>
                                                    <span className="order-price-final">₹{finalPrice}</span>
                                                </>
                                            ) : <>₹{rawPrice}</>}
                                        </div>
                                        <div className="order-card-status">
                                            {statusSelect(order)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <Pagination />
                    </div>
                </>
            )}
        </div>
    );
}

export default Orders;
